import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faQuestionCircle,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { MemreFlexBox } from 'components/core';
import * as colours from 'core/colors';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LearnMoreProps {
  children: React.ReactNode;
  width?: number;
  title?: string;
  type?: 'question' | 'info' | 'warning' | 'error' | 'success' | 'help';
}

export default function LearnMore({ children, width = 400, title = 'Learn More', type = 'question' }: LearnMoreProps) {
  const { t } = useTranslation();
  const helpText = t(title);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const icon =
    type === 'question'
      ? faQuestionCircle
      : type === 'info'
      ? faInfoCircle
      : type === 'warning'
      ? faExclamationTriangle
      : type === 'error'
      ? faExclamationCircle
      : type === 'success'
      ? faCheckCircle
      : faQuestionCircle;

  const iconColor =
    type === 'question'
      ? colours.primaryBlue
      : type === 'info'
      ? colours.primaryBlue
      : type === 'warning'
      ? colours.darkOrange
      : type === 'error'
      ? colours.veryDarkOrange
      : type === 'success'
      ? colours.primaryGreen
      : colours.primaryBlue;

  return (
    <>
      <Typography variant="caption" sx={{ color: iconColor, mr: 1 }}>
        <MemreFlexBox direction="row" gap={0.5} align="center">
          <button
            type="button"
            onClick={handleOpen}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={helpText}
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {helpText} <FontAwesomeIcon icon={icon} />
          </button>
        </MemreFlexBox>
      </Typography>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width,
            bgcolor: 'background.paper',
            boxShadow: 24,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            p: 3,
          }}
        >
          <Box sx={{ position: 'absolute', top: 3, right: 3 }}>
            <IconButton onClick={handleClose} aria-label="close">
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          </Box>
          {children}
        </Box>
      </Modal>
    </>
  );
}
